import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { useCallback, useMemo } from 'react';
import { getSearchableFieldNames } from '../../getSearchableFieldNames.ts';
import { type SearchableField } from '../../ISearchableField.tsx';

/**
 * Propaga al form ESTERNO i valori applicati dall'editor di un filtro
 * (`FilterBarEditPopoverContent`) e fa partire il submit esterno
 * (→ `setQuery` → URL).
 *
 * Usare SOLO nel body di un componente che vive SOPRA il `FormHandler`
 * annidato dell'editor, così `useFormSetValue`/`useFormAugmentedContext`
 * risolvono sull'outer:
 *   SI `FilterBarChip`, `FilterBarAddPopoverEditStep`
 *
 * NON usare dentro al `FormHandler` annidato (= dentro l'editor), dove i
 * context risolverebbero sull'INNER e i valori finirebbero sul form sbagliato:
 *   NO `FilterBarEditPopoverContentBody` e qualunque widget reso da
 *      `AdvancedSearchField`
 */
export function useApplyFilterValues(field: SearchableField) {
  const setValue = useFormSetValue();
  const augmented = useFormAugmentedContext();
  const fieldNames = useMemo(() => getSearchableFieldNames(field), [field]);

  return useCallback(
    (innerValues: Record<string, unknown>) => {
      // Iteriamo i name ATTESI del field, NON `Object.entries(innerValues)`.
      // Sembra equivalente ma non lo è: così garantiamo una `setValue` per OGNI
      // name anche quando il valore è assente/`undefined` — indispensabile per
      // SVUOTARE un filtro. Iterando le sole chiavi presenti nel payload, un
      // campo svuotato (chiave mancante) non verrebbe toccato → sul form esterno
      // resta il valore vecchio → torna il bug "il valore non si svuota davvero"
      // su multi-select/radio. NON "semplificare" in `Object.entries`.
      fieldNames.forEach(name => {
        const raw = innerValues[name];
        // Svuotare un filtro deve dare un campo ASSENTE, non `null`/`''`: lo
        // schema (rigido) del form esterno accetta un campo opzionale
        // `undefined`, ma rifiuta `null` (`mixed.notNull`) e `''` (`oneOf`).
        // Normalizziamo quindi i vuoti a `undefined` PRIMA del `setValue`, così:
        //  - la validazione esterna passa (campo opzionale assente) → niente
        //    reject del submit (niente overlay "[object Object]" in dev);
        //  - la query resta pulita (i campi `undefined` non finiscono in URL).
        // `0`/`false` sono valori validi: NON vanno normalizzati.
        const value = raw === null || raw === '' ? undefined : raw;
        setValue(name, value, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      });
      augmented.submitForm.current?.();
    },
    [fieldNames, setValue, augmented],
  );
}
